import {useEffect, useState} from "react";
import {useChatRoom, useGetRoom} from "../../../hooks/useChat";
import Loader from "../shared/Loader";
import ChatHeader from "./chatHeader";
import ChatMessages from "./chatMessages";
import MessageInput from "./messageInput";
import {socket} from "../../../socket/socket";
import {useOfferMessages} from "../../../hooks/useOfferMessages";
import {useQueryClient} from "@tanstack/react-query";
import {chatApi} from "../../../services/ChatService";

const MainChat = ({
  emptyConversation,
  setEmptyConversation,
  selectedConversation,
  user,
  setIsSidebarOpen,
  // messageLocalList,
  setConversations,
  // messageLoading,
}) => {
  const [loading, setLoading] = useState(false);
  const [joinedRoomId, setJoinedRoomId] = useState(null);
  const [joinError, setJoinError] = useState("");

  const {
    messageLocalList,
    messageLoading,
    setMessageLoading,
    addMessage,
  } = useOfferMessages({
    socket,
    selectedConversation,
  });

  const {isError} = useGetRoom({
    roomID: selectedConversation.id,
  });
  const {onDeleteRoom} = useChatRoom();
  const queryClient = useQueryClient();
  useEffect(() => {
    const propsitions = !!selectedConversation?.direct
      ? selectedConversation?.directChat?.propositions
      : selectedConversation?.indirectChat?.offer?.Offer_Proposals?.filter(
          (offProp) =>
            offProp?.technicianId === selectedConversation?.technicianId
        );

    if (!selectedConversation?.ChatMessages?.length && !propsitions?.length) {
      setEmptyConversation(selectedConversation);
    }
    const handleCleanup = () => {
      socket.emit("leave_room", String(selectedConversation.id));

      if (!!emptyConversation?.id) {
        onDeleteRoom({id: emptyConversation?.id});
      }
      // Optional: prevent the user from leaving (for test)
      // event.preventDefault();
      // event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleCleanup);

    return () => {
      const nextUrl = window.location.href;
      const isNextNotMessage = !nextUrl.includes("messages");
      if (
        !selectedConversation?.ChatMessages?.length &&
        !propsitions?.length &&
        isNextNotMessage &&
        !!emptyConversation?.id
      ) {
        onDeleteRoom({id: selectedConversation?.id});
        // setNewConversation(selectedConversation);
      }
      window.removeEventListener("beforeunload", handleCleanup);
    };
  }, [selectedConversation?.id, emptyConversation]);

  useEffect(() => {
    const roomId = String(selectedConversation.id);
    let active = true;
    const joinRoom = () => {
      if (!socket.connected) return;
      setJoinError("");
      setJoinedRoomId(null);
      socket.timeout(12000).emit("join_room", roomId, (error, ack) => {
        if (!active) return;
        if (!error && ack?.status === "success") {
          setJoinedRoomId(roomId);
        } else {
          setJoinError(ack?.message || "Connexion à la conversation impossible.");
          setMessageLoading(false);
        }
      });
    };
    const clearJoinedRoom = () => setJoinedRoomId(null);
    const handleConnectError = () => {
      if (!active) return;
      setJoinedRoomId(null);
      setJoinError("Connexion à la messagerie impossible. Reconnexion en cours...");
      setMessageLoading(false);
    };
    setJoinedRoomId(null);
    socket.on("connect", joinRoom);
    socket.on("disconnect", clearJoinedRoom);
    socket.on("connect_error", handleConnectError);
    if (socket.connected) {
      joinRoom();
    } else {
      socket.auth = {
        authorization: "Bearer " + localStorage.getItem("token"),
      };
      socket.connect();
    }

    return () => {
      active = false;
      socket.off("connect", joinRoom);
      socket.off("disconnect", clearJoinedRoom);
      socket.off("connect_error", handleConnectError);
      socket.emit("leave_room", roomId);
    };
  }, [selectedConversation.id, setMessageLoading]);

  const handleSendMessage = async (message) => {
    if (selectedConversation?.blocked) {
      throw new Error("Cette conversation est bloquée. Aucun message ou fichier ne peut être envoyé.");
    }
    if (!socket.connected) {
      socket.auth = {
        authorization: "Bearer " + localStorage.getItem("token"),
      };
      socket.connect();
    }

    let savedMessage;
    if (message.imageFile?.rawFile) {
      try {
        const response = await chatApi.sendAttachment({
          roomId: selectedConversation.id,
          text: message.text || "",
          file: message.imageFile.rawFile,
        });
        savedMessage = response?.data;
      } catch (error) {
        const payload = error?.response?.data;
        if (payload?.code === "CONTACT_DISCLOSURE_DETECTED") {
          error.code = payload.code;
          error.message = payload.error;
          error.violationCount = payload.details?.violationCount;
          error.accountBlocked = payload.details?.accountBlocked;
        }
        throw error;
      }
    } else {
      if (joinedRoomId !== String(selectedConversation.id)) {
        throw new Error("Connexion à la conversation en cours. Réessayez.");
      }
      const ack = await new Promise((resolve, reject) => {
      const args = [
        "send_message",
        message.text || "",
        null,
      ];
      args.push((error, ack) => {
        if (error) {
          reject(new Error("Le serveur n'a pas confirmé l'envoi du message."));
          return;
        }
        if (ack?.status !== "success") {
          const sendError = new Error(
            ack?.message || "Échec de l'envoi du message.",
          );
          sendError.code = ack?.code;
          sendError.violationCount = ack?.violationCount;
          sendError.accountBlocked = ack?.accountBlocked;
          reject(sendError);
          return;
        }
        resolve(ack);
      });
        socket.timeout(15000).emit(...args);
      });
      savedMessage = ack?.chatMessage;
    }

    addMessage(savedMessage);
    if (selectedConversation?.id === emptyConversation?.id) {
      setEmptyConversation(null);
    }
    setConversations((previous) => {
      const current = previous.find((room) => room.id === selectedConversation.id);
      if (!current) return previous;
      return [
        {...current, ChatMessages: savedMessage ? [savedMessage] : current.ChatMessages},
        ...previous.filter((room) => room.id !== selectedConversation.id),
      ];
    });
    queryClient.invalidateQueries({queryKey: ["chatRooms"]});
  };

  if (messageLoading) {
    return (
      // <div className="w-full relative">
      //   <Loader className={"absolute top-[50%] right-[50%]"} />
      // </div>
      <div className="flex flex-grow">
        <Loader className={"!relative "} />
      </div>
    );
  }
  if (joinError) {
    return (
      <div className="w-full h-full flex justify-center items-center text-red text-lg text-center">
        {joinError}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="w-full h-full flex justify-center items-center text-red text-lg text-center">
        erreur survenue à la recuperation de la chat
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-grow w-full sm:w-auto relative">
      {selectedConversation?.blocked && (
        <div className="absolute top-0 left-0 w-full h-full bg-[#000] bg-opacity-40 z-10"></div>
      )}
      <ChatHeader
        setIsSidebarOpen={setIsSidebarOpen}
        conversation={selectedConversation}
        role={user?.selectedRole}
        ConnectedUser={user}
      />
      <ChatMessages
        loading={loading}
        conversation={selectedConversation}
        messages={messageLocalList}
        user={user}
      />
      <></>{" "}
      <MessageInput
        blocked={selectedConversation?.blocked}
        disabled={joinedRoomId !== String(selectedConversation.id)}
        userId={user?.Technician?.id || user?.Client?.id}
        onSendMessage={handleSendMessage}
        setLoading={setLoading}
        loading={loading}
      />
    </div>
  );
};
export default MainChat;
